/* © NXAPP Limited */
import { useState, useEffect, Suspense } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { describeError } from '@/lib/describeError'
import {
  Plus, Trash2, Clock, ChevronDown, ChevronRight,
  PauseCircle, PlayCircle, XCircle, MessageSquare, GraduationCap, Search, Zap, Lock, Unlock, RotateCcw,
} from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import { PLAIN_STARTER_HTML } from '@/components/builder/plainStarter'
import MergeTagHelp from '@/components/builder/MergeTagHelp'
import { authedFetch } from '@/lib/authedFetch'

const SimpleEmailEditor = lazyWithReload(() => import('@/components/builder/SimpleEmailEditor'))

type DelayUnit = 'minutes' | 'hours' | 'days'

/**
 * A delay shorter than a day is rarely honoured literally: the runner skips the
 * campaign outside its sending window, so "+1 minute" really means "the next
 * slot inside 09:00–17:00 on a weekday". Saying so here stops the editor from
 * promising a timing the engine will not deliver.
 */
function windowCaveat(
  delayValue: number,
  delayUnit: DelayUnit,
  w?: { window_start_hour: number; window_end_hour: number; window_weekdays_only: boolean },
): string | null {
  if (!w) return null
  const hours = delayUnit === 'days' ? delayValue * 24 : delayUnit === 'hours' ? delayValue : delayValue / 60
  if (hours >= 24) return null          // a multi-day gap swamps any window drift
  const ref = new Date()
  const start = ukHourLabel(w.window_start_hour, ref)
  const end = ukHourLabel(w.window_end_hour, ref)
  return `arrives at the next slot in ${start.time}–${end.time} ${start.zone}${w.window_weekdays_only ? ', weekdays' : ''}`
}

interface StepRow {
  id: string; campaign_id: string; step_no: number
  delay_value: number; delay_unit: DelayUnit
  subject: string; html: string
}

interface EnrolRow {
  id: string; contact_id: string; status: string
  last_step_no: number; next_due_at: string | null; enrolled_at: string
  hold_reason: string | null
  graduated_to_campaign_id: string | null
  contacts: { email: string; first_name: string | null; company: string | null; status: string } | null
}

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-green-50 text-green-700 border-green-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-gray-100 text-gray-600 border-gray-200',
  stopped: 'bg-red-50 text-red-600 border-red-200',
  unsubscribed: 'bg-red-50 text-red-600 border-red-200',
}
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', on_hold: 'On hold — replied', completed: 'Finished',
  stopped: 'Stopped', unsubscribed: 'Unsubscribed',
}

/**
 * Reconcile the two status fields so the roster never contradicts the member /
 * contacts list. The CONTACT's status is the source of truth for "can we email
 * them"; the enrolment status only describes their position in THIS sequence.
 * The collision is the word "unsubscribed": an enrolment can read that while the
 * contact is a live subscriber (e.g. after a resubscribe). We resolve it here.
 */
function reconciledStatus(enrolStatus: string, contactStatus?: string): { label: string; style: string; note?: string } {
  const optedOut = !!contactStatus && contactStatus !== 'SUBSCRIBED'
  if (enrolStatus === 'unsubscribed') {
    return optedOut
      ? { label: 'Unsubscribed', style: STATUS_STYLE.unsubscribed, note: 'Opted out of all email' }
      // Contact is still subscribed — they just left this path. Not an opt-out.
      : { label: 'Left sequence', style: 'bg-gray-100 text-gray-600 border-gray-200', note: 'Still a subscriber — Resume to continue' }
  }
  const base = { label: STATUS_LABEL[enrolStatus] ?? enrolStatus, style: STATUS_STYLE[enrolStatus] ?? '' }
  // Even an "active" enrolment must show a contact-level opt-out, so the two
  // pages agree. (The sender re-checks and won't email them regardless.)
  return optedOut ? { ...base, note: 'Opted out of all email' } : base
}

/** One (enrolment, step) delivery cell — what actually happened to that email. */
function StepCell({ st, due }: {
  st?: { status: string; opened: boolean; clicked: boolean; complained: boolean; sentAt: string | null }
  /** When set, this step hasn't sent yet — its committed or projected due time. */
  due?: { at: string; estimated: boolean }
}) {
  if (!st) {
    if (due) {
      if (due.estimated) {
        // Projected from delays — shifts if an earlier step sends late.
        return (
          <div className="flex flex-col items-center gap-0.5">
            <span
              title="Estimated — moves with when the previous email actually sends"
              className="text-[11px] px-1.5 py-0.5 rounded border border-dashed bg-white text-gray-400 border-gray-300"
            >
              scheduled
            </span>
            <span className="text-[10px] text-gray-400 whitespace-nowrap">~ {stamp(due.at)}</span>
          </div>
        )
      }
      const overdue = new Date(due.at).getTime() <= Date.now()
      return (
        <div className="flex flex-col items-center gap-0.5">
          <span
            title={overdue ? 'Due now — will go out on the next check' : 'Committed — will go out at this time'}
            className="text-[11px] px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-300"
          >
            {overdue ? 'due now' : 'due'}
          </span>
          <span className="text-[10px] text-gray-400 whitespace-nowrap">{stamp(due.at)}</span>
        </div>
      )
    }
    return <span className="text-gray-300 text-[11px]" title="Not scheduled yet">·</span>
  }
  // Order matters: a complaint or bounce outranks an open.
  let badge: JSX.Element
  if (st.complained) badge = <span title="Marked as spam" className="text-[11px] px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-300">spam</span>
  else if (st.status === 'BOUNCED') badge = <span title="Bounced" className="text-[11px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">bounce</span>
  else if (st.status === 'FAILED') badge = <span title="Send failed" className="text-[11px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">failed</span>
  else if (st.clicked) badge = <span title="Clicked a link" className="text-[11px] px-1.5 py-0.5 rounded border bg-blue-600 text-white border-blue-700">click</span>
  else if (st.opened) badge = <span title="Opened" className="text-[11px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-300">open</span>
  else if (st.status === 'QUEUED') badge = <span title="Queued — will go out on the next check" className="text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">queued</span>
  else badge = <span title="Delivered, not yet opened" className="text-[11px] px-1.5 py-0.5 rounded border bg-gray-50 text-gray-500 border-gray-200">sent</span>
  // Show WHEN it was sent under the badge so status is legible at a glance.
  return (
    <div className="flex flex-col items-center gap-0.5">
      {badge}
      {st.sentAt && <span className="text-[10px] text-gray-400 whitespace-nowrap">{stamp(st.sentAt)}</span>}
    </div>
  )
}

function whenDue(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = d.getTime() - Date.now()
  if (diff <= 0) return 'due now'
  const hours = Math.round(diff / 3_600_000)
  if (hours < 24) return `in ${hours}h`
  return `in ${Math.round(hours / 24)}d`
}

/**
 * Compact absolute stamp in UK time (Europe/London), e.g. "22 Jul 10:05 BST".
 * Intl handles the BST/GMT switch, so the label is always right for the date.
 */
function stamp(iso: string | null): string {
  if (!iso) return ''
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZoneName: 'short',
  }).format(new Date(iso)).replace(',', '')
}

/** The UK clock hour for a given UTC hour on `ref`'s date, e.g. 9 UTC → "10:00" in summer. */
function ukHourLabel(utcHour: number, ref: Date): { time: string; zone: string } {
  const d = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate(), utcHour, 0, 0))
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
  }).formatToParts(d)
  const hh = parts.find(p => p.type === 'hour')?.value ?? String(utcHour).padStart(2, '0')
  const mm = parts.find(p => p.type === 'minute')?.value ?? '00'
  const zone = parts.find(p => p.type === 'timeZoneName')?.value ?? 'UTC'
  return { time: `${hh}:${mm}`, zone }
}

// ── Projecting future step times ────────────────────────────────────────────
// Steps 2+ have configured delays but no committed time until the previous step
// actually sends. We can still ESTIMATE by chaining from the next-due time
// through the sending window — mirroring the server's nextWindowSlot in London
// time. Shown as "~" because the estimate shifts if a step sends late.

const DELAY_MS: Record<DelayUnit, number> = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 }

function londonParts2(at: Date): { y: number; m: number; d: number; hour: number; weekday: number } {
  const p: Record<string, string> = {}
  for (const part of new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hourCycle: 'h23', weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
  }).formatToParts(at)) if (part.type !== 'literal') p[part.type] = part.value
  const wk: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  return { y: +p.year, m: +p.month, d: +p.day, hour: +p.hour, weekday: wk[p.weekday] ?? 0 }
}
function londonOffsetMs2(at: Date): number {
  const p: Record<string, string> = {}
  for (const part of new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at)) if (part.type !== 'literal') p[part.type] = part.value
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - at.getTime()
}
function londonWallClockToUtc2(y: number, m: number, d: number, hour: number): Date {
  const guess = Date.UTC(y, m - 1, d, hour, 0, 0)
  return new Date(guess - londonOffsetMs2(new Date(guess)))
}

interface SendWindow { window_start_hour: number; window_end_hour: number; window_weekdays_only: boolean }

function nextWindowSlotUK(from: Date, w: SendWindow): Date {
  let cursor = new Date(from.getTime())
  const nextDayOpen = (at: Date): Date => {
    const p = londonParts2(at)
    const np = londonParts2(new Date(londonWallClockToUtc2(p.y, p.m, p.d, 12).getTime() + 24 * 3_600_000))
    return londonWallClockToUtc2(np.y, np.m, np.d, w.window_start_hour)
  }
  for (let g = 0; g < 14; g++) {
    const { y, m, d, hour, weekday } = londonParts2(cursor)
    const weekend = weekday === 0 || weekday === 6
    if (w.window_weekdays_only && weekend) { cursor = nextDayOpen(cursor); continue }
    if (hour < w.window_start_hour) return londonWallClockToUtc2(y, m, d, w.window_start_hour)
    if (hour >= w.window_end_hour) { cursor = nextDayOpen(cursor); continue }
    return cursor
  }
  return cursor
}

/**
 * Per-step due times for one enrolment. The next step is committed (from
 * next_due_at); each later step is projected by adding its delay to the prior
 * step's time and snapping into the window. `estimated` flags the projections.
 */
function projectDueTimes(
  e: EnrolRow, steps: StepRow[], w?: SendWindow,
): Map<number, { at: string; estimated: boolean }> {
  const out = new Map<number, { at: string; estimated: boolean }>()
  if (e.status !== 'active' || !e.next_due_at) return out
  const ordered = [...steps].sort((a, b) => a.step_no - b.step_no)
  let prev = new Date(e.next_due_at)
  for (const st of ordered) {
    if (st.step_no <= e.last_step_no) continue
    if (st.step_no === e.last_step_no + 1) {
      out.set(st.step_no, { at: e.next_due_at, estimated: false })
      continue
    }
    if (!w) break                          // no window loaded → can't project reliably
    const raw = new Date(prev.getTime() + Math.max(0, st.delay_value) * DELAY_MS[st.delay_unit])
    const due = nextWindowSlotUK(raw, w)
    out.set(st.step_no, { at: due.toISOString(), estimated: true })
    prev = due
  }
  return out
}

/** Re-render every `ms` so live countdowns tick. */
function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/**
 * Approx time to the next automatic send-check. The scheduler (cron-job.org)
 * fires every 15 minutes on the wall-clock quarter-hours, so we count down to
 * the next :00/:15/:30/:45. Approximate by nature — a tick can arrive a little
 * late — hence the "~".
 */
function nextTickIn(now: number): string {
  const period = 15 * 60_000
  const ms = period - (now % period)
  const mins = Math.floor(ms / 60_000)
  const secs = Math.floor((ms % 60_000) / 1000)
  return mins > 0 ? `~${mins}m ${secs}s` : `~${secs}s`
}

export default function SequenceContent({ campaignId }: { campaignId: string }) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [openStep, setOpenStep] = useState<string | null>(null)
  const [filterStep, setFilterStep] = useState<string>('all')     // 'all' | '0' | '1' | …
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [search, setSearch] = useState('')
  const now = useNow()   // drives the "next auto-check" countdown
  // Per-email columns are the default: they show what actually happened to each
  // send at a glance. Collapsible to a one-line summary for many-step sequences.
  const [splitSteps, setSplitSteps] = useState(true)
  // A live sequence is editing history as much as configuration: people have
  // already received these emails, and later enrolees will receive whatever is
  // here now. Lock it so a stray keystroke cannot silently change what goes out.
  const [unlocked, setUnlocked] = useState(false)

  const { data: steps, isLoading } = useQuery({
    queryKey: ['sequence-steps', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase.from('sequence_steps')
        .select('*').eq('campaign_id', campaignId).order('step_no')
      if (error) throw error
      return (data ?? []) as StepRow[]
    },
  })

  const { data: enrolments } = useQuery({
    queryKey: ['sequence-enrolments', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase.from('sequence_enrollments')
        .select('id, contact_id, status, last_step_no, next_due_at, enrolled_at, hold_reason, graduated_to_campaign_id, contacts(email, first_name, company, status)')
        .eq('campaign_id', campaignId)
        .order('enrolled_at', { ascending: false })
      if (error) throw error
      return (data ?? []).map(r => ({
        ...r,
        contacts: Array.isArray(r.contacts) ? r.contacts[0] : r.contacts,
      })) as EnrolRow[]
    },
  })

  // Delivery state per (enrolment, step): sent/bounced/failed from sends, plus
  // opened/clicked from events. One pair of queries for the whole table.
  const { data: stepStatus } = useQuery({
    queryKey: ['sequence-step-status', campaignId],
    queryFn: async () => {
      const { data: sends } = await supabase
        .from('sends')
        .select('id, enrollment_id, sequence_step_id, status, sent_at')
        .not('enrollment_id', 'is', null)
      const ids = (sends ?? []).map(s => s.id as string)
      const events = ids.length
        ? (await supabase.from('events').select('send_id, type').in('send_id', ids)).data ?? []
        : []
      const evBySend = new Map<string, Set<string>>()
      for (const e of events) {
        const k = e.send_id as string
        if (!evBySend.has(k)) evBySend.set(k, new Set())
        evBySend.get(k)!.add(e.type as string)
      }
      // key: `${enrollment_id}:${sequence_step_id}`
      const m = new Map<string, { status: string; opened: boolean; clicked: boolean; complained: boolean; sentAt: string | null }>()
      for (const s of sends ?? []) {
        const ev = evBySend.get(s.id as string) ?? new Set<string>()
        m.set(`${s.enrollment_id}:${s.sequence_step_id}`, {
          status: s.status as string,
          opened: ev.has('OPEN'),
          clicked: ev.has('CLICK'),
          complained: ev.has('COMPLAINT'),
          sentAt: (s.sent_at as string) ?? null,
        })
      }
      return m
    },
  })

  // Campaign settings (graduation target) + the list of campaigns to pick from
  const { data: campaign } = useQuery({
    queryKey: ['campaign-graduation', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase.from('campaigns')
        .select('id, graduate_to_campaign_id, sending_profile_id, window_start_hour, window_end_hour, window_weekdays_only')
        .eq('id', campaignId).single()
      if (error) throw error
      return data as {
        id: string; graduate_to_campaign_id: string | null; sending_profile_id: string | null
        window_start_hour: number; window_end_hour: number; window_weekdays_only: boolean
      }
    },
  })

  const { data: otherCampaigns } = useQuery({
    queryKey: ['campaigns-for-graduation', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('campaigns')
        .select('id, name, kind').eq('user_id', user!.id).order('name')
      return (data ?? []).filter(c => c.id !== campaignId) as { id: string; name: string; kind: string }[]
    },
  })

  // Footer identity for step previews — same chain the server uses when sending:
  // the campaign's profile, falling back to the default profile.
  const { data: profiles } = useQuery({
    queryKey: ['sending-profiles-identity', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('sending_profiles')
        .select('id, from_name, is_default, company_name, company_address, company_website')
        .eq('user_id', user!.id)
      return (data ?? []) as {
        id: string; from_name: string; is_default: boolean
        company_name: string | null; company_address: string | null; company_website: string | null
      }[]
    },
  })

  const activeProfile =
    (profiles ?? []).find(p => p.id === campaign?.sending_profile_id) ??
    (profiles ?? []).find(p => p.is_default) ??
    (profiles ?? [])[0]
  const identity = {
    companyName: activeProfile?.company_name?.trim() || activeProfile?.from_name || '',
    companyAddress: activeProfile?.company_address?.trim() || '',
    companyWebsite: activeProfile?.company_website?.trim() || '',
  }

  const setGraduation = useMutation({
    mutationFn: async (targetId: string | null) => {
      const { error } = await supabase.from('campaigns')
        .update({ graduate_to_campaign_id: targetId }).eq('id', campaignId)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-graduation', campaignId] }),
  })

  const addStep = useMutation({
    mutationFn: async () => {
      const nextNo = (steps?.length ?? 0) + 1
      const { error } = await supabase.from('sequence_steps').insert({
        campaign_id: campaignId,
        user_id: user!.id,
        step_no: nextNo,
        // Step 1 goes out an hour after enrolment; later steps default to 3 days.
        delay_value: nextNo === 1 ? 1 : 3,
        delay_unit: nextNo === 1 ? 'hours' : 'days',
        subject: '',
        html: PLAIN_STARTER_HTML,
        design_json: { mode: 'simple' },
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequence-steps', campaignId] }),
  })

  const updateStep = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<StepRow> }) => {
      const { error } = await supabase.from('sequence_steps').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequence-steps', campaignId] }),
  })

  const delStep = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sequence_steps').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sequence-steps', campaignId] }),
  })

  // Fire the scheduler on demand. The cron runs every 15 minutes, which makes a
  // one-minute step delay impossible to test — and leaves "nothing sent"
  // ambiguous between not-yet-due and scheduler-never-ran.
  const runNow = useMutation({
    mutationFn: async () => {
      const res = await authedFetch('/api/sequences/run', { method: 'POST' })
      if (!res.ok) throw new Error(await res.text())
      return await res.json() as { sent: number; campaigns: number; diagnostics?: string[] }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequence-enrolments', campaignId] })
    },
  })

  const setEnrolStatus = useMutation({
    mutationFn: async ({ id, status, reason, restart, reinstate, contactId }:
      { id: string; status: string; reason?: string; restart?: boolean; reinstate?: boolean; contactId?: string | null }) => {
      // Reinstate = fully clear the opt-out so a resume actually STICKS. Without
      // this, the runner re-checks brand suppression each tick and immediately
      // flips the enrolment back to unsubscribed. So we clear ALL three states:
      // brand-scoped suppression, the global contact flag, and stamp the audit.
      if (reinstate && contactId) {
        const { error: eu } = await supabase.from('unsubscribes')
          .delete().eq('contact_id', contactId).eq('user_id', user!.id)
        if (eu) throw eu
        const { error: ec } = await supabase.from('contacts')
          .update({ status: 'SUBSCRIBED', unsubscribed_at: null })
          .eq('id', contactId).eq('user_id', user!.id)
        if (ec) throw ec
        await supabase.from('suppression_events')
          .update({ overridden_at: new Date().toISOString(), overridden_reason: 'Reinstated from sequence' })
          .eq('contact_id', contactId).is('overridden_at', null)
        // Bring the campaign membership back in step, so the "lead member" view
        // agrees with the sequence roster.
        await supabase.from('lead_campaign_assignments')
          .update({ status: 'active', unsubscribed_at: null })
          .eq('contact_id', contactId).eq('user_id', user!.id).eq('status', 'unsubscribed')
      }
      const patch: Record<string, unknown> = { status, hold_reason: reason ?? null }
      // Resuming: next step becomes due immediately (the window still applies).
      if (status === 'active') patch.next_due_at = new Date().toISOString()
      if (status === 'stopped' || status === 'on_hold') patch.next_due_at = null
      // Restart walks the sequence again from step 1.
      if (restart) patch.last_step_no = 0
      const { error } = await supabase.from('sequence_enrollments').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      // One change can touch enrolment, contact, membership and suppression —
      // refresh every view that reads them so nothing shows stale.
      for (const k of [['sequence-enrolments'], ['contacts'], ['campaign_members'], ['brand-optouts'], ['suppression'], ['contact-memberships']]) {
        qc.invalidateQueries({ queryKey: k })
      }
    },
  })

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>

  const counts = (enrolments ?? []).reduce<Record<string, number>>((a, e) => {
    a[e.status] = (a[e.status] ?? 0) + 1; return a
  }, {})

  // How many people are sitting at each step — the "what stage is everyone at" view
  const stepCounts = (enrolments ?? []).reduce<Record<number, number>>((a, e) => {
    a[e.last_step_no] = (a[e.last_step_no] ?? 0) + 1; return a
  }, {})

  // "Started" means at least one step has actually gone out. Enrolled-but-not-yet-
  // sent is still safe to edit, and locking then would be needless friction.
  const hasSent = (enrolments ?? []).some(e => e.last_step_no > 0)
  const locked = hasSent && !unlocked

  const q = search.trim().toLowerCase()
  const visible = (enrolments ?? []).filter(e =>
    (filterStep === 'all' || String(e.last_step_no) === filterStep) &&
    (filterStatus === 'all' || e.status === filterStatus) &&
    (!q ||
      e.contacts?.email?.toLowerCase().includes(q) ||
      e.contacts?.first_name?.toLowerCase().includes(q) ||
      e.contacts?.company?.toLowerCase().includes(q)),
  )

  return (
    <div className="space-y-8">
      {/* ── Steps ─────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Email sequence</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Everyone walks this path from their own join date. Step 1 is timed from enrolment;
              each later step from when the previous one actually sent.
            </p>
          </div>
          <button onClick={() => addStep.mutate()} disabled={addStep.isPending || locked} className="btn-primary text-sm disabled:opacity-40">
            <Plus className="w-4 h-4" /> Add step
          </button>
        </div>

        {addStep.isError && (
          <p className="text-xs text-red-600 font-mono">{describeError(addStep.error)}</p>
        )}

        {hasSent && (
          <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
            locked ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-300'}`}>
            {locked
              ? <Lock className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
              : <Unlock className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-medium ${locked ? 'text-gray-700' : 'text-amber-900'}`}>
                {locked ? 'This sequence is live — editing locked' : 'Editing unlocked — changes affect everyone still to receive these emails'}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Emails have already been sent from this sequence. Anyone enrolled later, and anyone
                mid-sequence, will receive whatever these steps say at the moment their step fires.
              </p>
            </div>
            <button
              onClick={() => {
                if (locked) {
                  if (confirm('Unlock editing?\n\nThis sequence is already sending. Changes take effect immediately for everyone who has not yet reached that step.')) setUnlocked(true)
                } else setUnlocked(false)
              }}
              className={`shrink-0 flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-lg border transition-colors ${
                locked
                  ? 'bg-white text-gray-600 border-gray-200 hover:border-brand-300 hover:text-brand-600'
                  : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'}`}
            >
              {locked ? <><Unlock className="w-3 h-3" /> Unlock to edit</> : <><Lock className="w-3 h-3" /> Lock again</>}
            </button>
          </div>
        )}

        {!steps?.length ? (
          <EmptyState
            icon={Clock}
            title="No steps yet"
            description="Add your first email — it goes out shortly after someone is enrolled."
          />
        ) : (
          <div className="space-y-2">
            {steps.map(step => (
              <div key={step.id} className="card overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => setOpenStep(openStep === step.id ? null : step.id)}
                    className="text-gray-400 hover:text-gray-700"
                  >
                    {openStep === step.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <span className="w-7 h-7 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0">
                    {step.step_no}
                  </span>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-xs text-gray-400">
                      {step.step_no === 1 ? 'after joining' : 'after previous'}
                    </span>
                    <input
                      type="number" min={0} defaultValue={step.delay_value} disabled={locked}
                      onBlur={e => {
                        const v = Number(e.target.value)
                        if (v !== step.delay_value) updateStep.mutate({ id: step.id, patch: { delay_value: v } })
                      }}
                      className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                    <select
                      defaultValue={step.delay_unit}
                      disabled={locked}
                      onChange={e => updateStep.mutate({ id: step.id, patch: { delay_unit: e.target.value as DelayUnit } })}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                    >
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                    {(() => {
                      const caveat = windowCaveat(step.delay_value, step.delay_unit, campaign)
                      return caveat
                        ? <span className="text-[11px] text-amber-600 whitespace-nowrap" title="Sending window is set per campaign to protect deliverability.">{caveat}</span>
                        : null
                    })()}
                  </div>

                  <input
                    id={`subject-${step.id}`}
                    defaultValue={step.subject}
                    disabled={locked}
                    placeholder="Subject line… (personalisation tags work here)"
                    onBlur={e => {
                      if (e.target.value !== step.subject) updateStep.mutate({ id: step.id, patch: { subject: e.target.value } })
                    }}
                    className="flex-1 min-w-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />

                  <button
                    onClick={() => { if (confirm(`Delete step ${step.step_no}?`)) delStep.mutate(step.id) }}
                    disabled={locked}
                    className="text-gray-300 hover:text-red-500 shrink-0 disabled:opacity-30 disabled:hover:text-gray-300"
                    title={locked ? 'Unlock the sequence to delete steps' : 'Delete step'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="px-4 pb-2 -mt-1">
                  <MergeTagHelp
                    onInsert={tag => {
                      const el = document.getElementById(`subject-${step.id}`) as HTMLInputElement | null
                      if (!el) return
                      const at = el.selectionStart ?? el.value.length
                      el.value = el.value.slice(0, at) + tag + el.value.slice(el.selectionEnd ?? at)
                      el.focus()
                      el.setSelectionRange(at + tag.length, at + tag.length)
                      updateStep.mutate({ id: step.id, patch: { subject: el.value } })
                    }}
                  />
                </div>

                {openStep === step.id && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50">
                    {locked ? (
                      // Read-only render. A disabled contentEditable is still
                      // typeable, so the editor is not mounted at all when locked.
                      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                        <div className="px-3 py-1.5 border-b border-gray-200 bg-gray-50 flex items-center gap-1.5">
                          <Lock className="w-3 h-3 text-gray-400" />
                          <span className="text-[11px] text-gray-500">Read-only — unlock above to edit</span>
                        </div>
                        <div
                          className="simple-editor px-6 py-5 text-[15px] leading-relaxed text-gray-800 max-h-[50vh] overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: step.html || '<p class="text-gray-400">Empty</p>' }}
                        />
                      </div>
                    ) : (
                      <Suspense fallback={<div className="flex justify-center py-12"><Spinner /></div>}>
                        <SimpleEmailEditor
                          initialHtml={step.html}
                          identity={identity}
                          onChange={h => updateStep.mutate({ id: step.id, patch: { html: h } })}
                        />
                      </Suspense>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── After the last step ───────────────────────────────────────── */}
      <section className="card p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
            <GraduationCap className="w-4.5 h-4.5 text-brand-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm">When the sequence finishes</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">
              Move them onto an ongoing campaign — typically the weekly digest — so outreach
              hands over to your regular newsletter automatically.
            </p>
            <select
              className="input max-w-md"
              value={campaign?.graduate_to_campaign_id ?? ''}
              onChange={e => setGraduation.mutate(e.target.value || null)}
            >
              <option value="">Do nothing — they just finish</option>
              {(otherCampaigns ?? []).map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.kind === 'sequence' ? ' (sequence)' : ''}
                </option>
              ))}
            </select>
            {setGraduation.isError && (
              <p className="mt-1 text-xs text-red-600 font-mono">{describeError(setGraduation.error)}</p>
            )}
            {campaign?.graduate_to_campaign_id && (
              <p className="mt-1.5 text-[11px] text-gray-400">
                Added to that campaign's members the moment the last step sends. If the target is
                itself a sequence, they start at its step 1.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ── Enrolments ────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h3 className="font-semibold text-gray-900">
            People in this sequence
            <span className="ml-2 text-xs font-normal text-gray-400">
              {counts.active ?? 0} active · {counts.on_hold ?? 0} on hold · {counts.completed ?? 0} finished
            </span>
          </h3>

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => runNow.mutate()}
              disabled={runNow.isPending}
              className="flex items-center gap-1.5 text-[11px] font-medium text-amber-800 hover:text-amber-900 px-2.5 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
              title="Send any steps that are due right now, instead of waiting for the 15-minute cron. Sends real email."
            >
              <Zap className="w-3 h-3" /> {runNow.isPending ? 'Running…' : 'Send due steps now'}
            </button>
            {runNow.isSuccess && (
              <div className="text-[11px] text-gray-500 basis-full mt-1">
                <span className="font-medium">
                  {runNow.data.sent === 0
                    ? 'Nothing sent this run — reasons below:'
                    : `Sent ${runNow.data.sent} email${runNow.data.sent === 1 ? '' : 's'}.`}
                </span>
                {runNow.data.diagnostics?.length ? (
                  <ul className="mt-1 space-y-0.5 font-mono text-[10px] text-gray-500">
                    {runNow.data.diagnostics.map((d, i) => <li key={i}>· {d}</li>)}
                  </ul>
                ) : null}
              </div>
            )}
            {runNow.isError && (
              <span className="text-[11px] text-red-600 font-mono">{describeError(runNow.error)}</span>
            )}
            <span
              className="ml-auto flex items-center gap-1.5 text-[11px] font-medium text-gray-500 px-2.5 py-1 rounded border border-gray-200 bg-gray-50 whitespace-nowrap"
              title="Due steps are sent automatically. The scheduler checks every 15 minutes on the quarter-hour — this is the approximate wait until the next check."
            >
              <Clock className="w-3 h-3 text-gray-400" />
              Next auto-check {nextTickIn(now)}
            </span>
            <button
              onClick={() => setSplitSteps(v => !v)}
              className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded border transition-colors ${
                splitSteps ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              title="Show a column per step with what happened to each email"
            >
              {splitSteps ? 'Hide per-email columns' : 'Split by email'}
            </button>
          </div>

          {/* Stage chips — click to filter to everyone sitting at that step */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <button
              onClick={() => setFilterStep('all')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                filterStep === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              All {enrolments?.length ?? 0}
            </button>
            <button
              onClick={() => setFilterStep('0')}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                filterStep === '0' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              Not started {stepCounts[0] ?? 0}
            </button>
            {(steps ?? []).map(st => (
              <button
                key={st.id}
                onClick={() => setFilterStep(String(st.step_no))}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  filterStep === String(st.step_no) ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                Had email {st.step_no} · {stepCounts[st.step_no] ?? 0}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email or company…"
                className="input pl-8 py-1.5 text-xs"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="input py-1.5 text-xs w-auto"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="on_hold">On hold — replied</option>
              <option value="completed">Finished</option>
              <option value="stopped">Stopped</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </div>
        </div>

        {!enrolments?.length ? (
          <EmptyState
            icon={MessageSquare}
            title="Nobody enrolled yet"
            description="Add leads to this campaign from the Lead Engine — they start at step 1 from the moment they join."
          />
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Contact</th>
                  {splitSteps
                    ? (steps ?? []).map(st => (
                        <th key={st.id} className="text-center px-2 py-2 font-medium whitespace-nowrap" title={st.subject || `Step ${st.step_no}`}>
                          #{st.step_no}
                        </th>
                      ))
                    : <th className="text-left px-4 py-2 font-medium">Progress</th>}
                  <th className="text-left px-4 py-2 font-medium">Next</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map(e => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <p className="font-medium text-gray-900">{e.contacts?.first_name || e.contacts?.email}</p>
                      <p className="text-xs text-gray-400">{e.contacts?.email}{e.contacts?.company ? ` · ${e.contacts.company}` : ''}</p>
                    </td>
                    {splitSteps
                      ? (() => {
                          const dueTimes = projectDueTimes(e, steps ?? [], campaign)
                          return (steps ?? []).map(st => {
                            const cell = stepStatus?.get(`${e.id}:${st.id}`)
                            return (
                              <td key={st.id} className="px-2 py-2 text-center">
                                <StepCell st={cell} due={cell ? undefined : dueTimes.get(st.step_no)} />
                              </td>
                            )
                          })
                        })()
                      : (
                        <td className="px-4 py-2 text-gray-600">
                          {e.last_step_no === 0 ? 'not started' : `step ${e.last_step_no} of ${steps?.length ?? 0} sent`}
                        </td>
                      )}
                    <td className="px-4 py-2 text-gray-500">
                      {e.status === 'active' ? (
                        <div className="flex flex-col leading-tight">
                          <span>{whenDue(e.next_due_at)}</span>
                          {e.next_due_at && <span className="text-[10px] text-gray-400 whitespace-nowrap">{stamp(e.next_due_at)}</span>}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      {(() => {
                        const rs = reconciledStatus(e.status, e.contacts?.status)
                        return (
                          <>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${rs.style}`}>
                              {rs.label}
                            </span>
                            {rs.note && <span className="block mt-0.5 text-[10px] text-gray-400">{rs.note}</span>}
                          </>
                        )
                      })()}
                      {e.graduated_to_campaign_id && (
                        <span className="block mt-0.5 text-[10px] text-gray-400">
                          → {(otherCampaigns ?? []).find(c => c.id === e.graduated_to_campaign_id)?.name ?? 'moved on'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {e.status === 'active' && (
                          <>
                            <button
                              title="They replied — put on hold"
                              onClick={() => setEnrolStatus.mutate({ id: e.id, status: 'on_hold', reason: 'replied' })}
                              className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50"
                            >
                              <PauseCircle className="w-4 h-4" />
                            </button>
                            <button
                              title="Stop the sequence for this person"
                              onClick={() => setEnrolStatus.mutate({ id: e.id, status: 'stopped' })}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {(e.status === 'on_hold' || e.status === 'stopped' || e.status === 'unsubscribed') && (
                          <button
                            title={e.last_step_no >= (steps?.length ?? 0)
                              ? 'All steps already sent — use Restart to send from step 1'
                              : `Resume — send step ${e.last_step_no + 1} next (continues where they stopped)`}
                            disabled={e.last_step_no >= (steps?.length ?? 0)}
                            onClick={() => {
                              const ok = e.status !== 'unsubscribed' || confirm(
                                `Resume ${e.contacts?.email ?? 'this contact'} from step ${e.last_step_no + 1}?\n\n` +
                                `This will REINSTATE their subscription (clearing the opt-out) so the sequence continues — they will NOT get earlier emails again. ` +
                                `Only do this if you have a lawful basis to email them; re-adding is not consent.`
                              )
                              if (ok) setEnrolStatus.mutate({
                                id: e.id, status: 'active',
                                reinstate: e.status === 'unsubscribed', contactId: e.contact_id,
                              })
                            }}
                            className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-30 disabled:hover:bg-transparent"
                          >
                            <PlayCircle className="w-4 h-4" />
                          </button>
                        )}
                        {(e.status === 'unsubscribed' || e.status === 'completed' || e.status === 'stopped') && (
                          <button
                            title="Restart this person from step 1 (re-sends email #1)"
                            onClick={() => confirm(
                              `Restart ${e.contacts?.email ?? 'this contact'} from step 1?\n\n` +
                              `They will receive email #1 AGAIN on the next send-check. To continue without repeating email 1, use Resume instead. ` +
                              `${e.status === 'unsubscribed' ? 'This will REINSTATE their subscription (clearing the opt-out). ' : ''}` +
                              `Only restart someone you have a lawful basis to email; re-adding is not consent.`
                            ) && setEnrolStatus.mutate({
                              id: e.id, status: 'active', restart: true,
                              reinstate: e.status === 'unsubscribed', contactId: e.contact_id,
                            })}
                            className="p-1.5 rounded-md text-gray-400 hover:text-brand-600 hover:bg-brand-50"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visible.length && (
              <p className="px-4 py-6 text-center text-sm text-gray-400">
                No one matches these filters.
              </p>
            )}
          </div>
        )}

        {setEnrolStatus.isError && (
          <p className="text-xs text-red-600 font-mono">{describeError(setEnrolStatus.error)}</p>
        )}
      </section>
    </div>
  )
}
